import { Extension, Mark, Node, mergeAttributes } from '@tiptap/core';

/**
 * TipTap StarterKit only ships extensions for paragraphs, headings, lists
 * and a handful of other "rich text" nodes. The contract document template
 * however relies on a number of structural wrappers (`<section>`, `<div>`,
 * `<header>`, ...) and on `class` attributes on virtually every element so
 * the user-supplied CSS in `cssContent` can target them.
 *
 * Without the extensions defined here, parsing the template into the editor
 * silently drops those wrappers and strips every `class` attribute. The
 * next round-trip through `editor.getHTML()` would then persist a lossy
 * version of the template and break the rendered PDF layout (see
 * `ContractDocumentEditor`).
 *
 * The extensions intentionally keep the model permissive: any block-level
 * content is allowed inside the structural containers and arbitrary
 * `class`/`style` attributes are preserved verbatim. They are not exposed
 * through the editor toolbar — they exist purely to faithfully round-trip
 * existing template markup.
 */

const classStyleAttributes = () => ({
    class: {
        default: null as string | null,
        parseHTML: (element: HTMLElement) => element.getAttribute('class'),
        renderHTML: (attributes: { class?: string | null }) =>
            attributes.class ? { class: attributes.class } : {},
    },
    style: {
        default: null as string | null,
        parseHTML: (element: HTMLElement) => element.getAttribute('style'),
        renderHTML: (attributes: { style?: string | null }) =>
            attributes.style ? { style: attributes.style } : {},
    },
});

const createStructuralBlock = (tag: 'div' | 'section' | 'header' | 'footer' | 'article') =>
    Node.create({
        name: `structural${tag.charAt(0).toUpperCase()}${tag.slice(1)}`,
        group: 'block',
        // Permit any block-level content (paragraphs, headings, tables,
        // lists, other structural blocks, images, …) without forcing the
        // wrapper to contain at least one child.
        content: 'block*',
        defining: true,
        addAttributes() {
            return classStyleAttributes();
        },
        parseHTML() {
            return [{ tag }];
        },
        renderHTML({ HTMLAttributes }) {
            return [tag, mergeAttributes(HTMLAttributes), 0];
        },
    });

export const StructuralDiv = createStructuralBlock('div');
export const StructuralSection = createStructuralBlock('section');
export const StructuralHeader = createStructuralBlock('header');
export const StructuralFooter = createStructuralBlock('footer');
export const StructuralArticle = createStructuralBlock('article');

/**
 * Inline `<span>` mark used by the default template (e.g. `<span class=
 * "contract-field">`). TipTap's `TextStyle` mark renders a `<span>` too but
 * only carries inline style attributes — it does not preserve `class`.
 */
export const ClassSpan = Mark.create({
    name: 'classSpan',
    inclusive: false,
    parseHTML() {
        return [
            {
                tag: 'span',
                getAttrs: node => {
                    if (typeof node === 'string') return false;
                    // Only claim spans that actually carry a class. Spans
                    // with only inline style attributes should remain owned
                    // by the `TextStyle` mark so font-family / font-size
                    // controls keep working.
                    return node.hasAttribute('class') ? null : false;
                },
            },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        return ['span', mergeAttributes(HTMLAttributes), 0];
    },
    addAttributes() {
        return classStyleAttributes();
    },
});

/**
 * Adds `class` and `style` attributes to every "rich" node TipTap already
 * understands, so the parser stops dropping them. This is what keeps the
 * `<table class="contract-extension-table">`, `<h1 class="contract-title">`,
 * `<ol class="contract-parties">`, `<img class="contract-logo">`, … markup
 * intact after a parse + serialize round-trip.
 */
export const PreserveClassAttributes = Extension.create({
    name: 'preserveClassAttributes',
    addGlobalAttributes() {
        return [
            {
                types: [
                    'paragraph',
                    'heading',
                    'bulletList',
                    'orderedList',
                    'listItem',
                    'blockquote',
                    'codeBlock',
                    'table',
                    'tableRow',
                    'tableHeader',
                    'tableCell',
                    'image',
                    'structuralDiv',
                    'structuralSection',
                    'structuralHeader',
                    'structuralFooter',
                    'structuralArticle',
                ],
                attributes: classStyleAttributes(),
            },
        ];
    },
});
